import { ComponentProps, FC, FormEvent, FormEventHandler, KeyboardEvent, ReactNode, useCallback, useEffect, useMemo } from 'react'
import styles from 'components/features/creditCard/creditCardOnboarding/InPlaceInput/InPlaceInput.styl'
import Button from 'components/features/pfm/Button/Button'
import { debounce } from 'lodash'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import MaskedInput from 'react-text-mask'
import * as yup from 'yup'
import { ObjectSchema } from 'yup'
import { schemaString } from 'schemas/baseSchema'
import { QAMarked } from 'interfaces/component'
import resolveErrorById from 'constants/errors'

const DEFAULT_SCHEMA = yup.object({
  value: schemaString,
})

type InPlaceInputProps = QAMarked & {
  label: ReactNode
  value: string
  mask?: (string | RegExp)[]
  validationSchema?: ObjectSchema<{ value: string } | undefined>
  maxLength?: number
  onSave: (value: string) => void
  onCancel: () => void
  submitOnBlur?: boolean
}
export const InPlaceInput: FC<InPlaceInputProps> = (props) => {
  const { label, value, onSave, onCancel, mask, qaMarker, qaType, submitOnBlur = true, validationSchema = DEFAULT_SCHEMA, maxLength } = props

  const formContext = useForm<{ value: string }>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      value,
    },
  })
  const { register, handleSubmit, formState: { errors }} = formContext

  const onSubmit = useMemo(() => handleSubmit(({ value: nextValue }) => {
    onSave(nextValue)
  })
  , [handleSubmit, onSave])

  const handleBlurDebounce = useMemo(() => {
    return !!submitOnBlur ? debounce(onSubmit, 200) : debounce(onCancel, 200)
  }, [onCancel, onSubmit, submitOnBlur])

  const handleSuccessSubmit = useMemo(() => {
    return (event?: FormEvent<HTMLFormElement>) => {
      handleBlurDebounce.cancel()
      onSubmit(event)
    }
  }, [handleBlurDebounce, onSubmit])

  useEffect(() => () => {
    handleBlurDebounce.cancel()
  }, [handleBlurDebounce])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSuccessSubmit()
    }
  }, [onCancel, handleSuccessSubmit])

  return (
    <FormProvider { ...formContext }>
      <form onSubmit={ handleSuccessSubmit }>
        <div className={ styles.inPlaceInput }>
          <div className={ styles.label } >{ label }</div>
          <div className={ styles.cols }>
            <div className={ styles.inputWrapper }>
              { !!mask && (
                <MaskedInputController
                  mask={ mask }

                  name='value'
                  className={ styles.input }
                  autoFocus
                  onKeyDown={ handleKeyDown }
                  onBlur={ handleBlurDebounce }
                  data-qa-marker={ qaMarker }
                  data-qa-type={ qaType }
                  maxLength={ maxLength }
                />
              )}
              { !mask && (
                <input
                  { ...register('value') }
                  className={ styles.input }
                  autoFocus
                  onKeyDown={ handleKeyDown }
                  onBlur={ handleBlurDebounce }
                  data-qa-marker={ qaMarker }
                  data-qa-type={ qaType }
                  maxLength={ maxLength }
                />
              )}
            </div>
            <div className={ styles.cta }>
              <Button style='textOnly' fullWidth submit size='micro' qaMarker='save'>
                SAVE
              </Button>
            </div>
          </div>
        </div>
        { errors.value?.message && (
          <div className={ styles.error }>
            { resolveErrorById(errors.value?.message as any) }
          </div>
        )}
      </form>
    </FormProvider>
  )
}


type MaskedInputControllerProps =
  & Omit<ComponentProps<typeof MaskedInput>, 'name' | 'ref'>
  & {
    name: string
  }

export const MaskedInputController: FC<MaskedInputControllerProps> = (props) => {
  const { name, ...restProps } = props

  const { control, trigger } = useFormContext<Record<string, any>>()

  const inputRenderer = useCallback(({ field: { value, onChange }}) => {
    const handleChange: FormEventHandler<HTMLInputElement> = (event) => {
      const target = event.target as HTMLInputElement
      onChange(target.value)
      trigger(name)
    }
    return (
      <MaskedInput value={ value } onChange={ handleChange } { ...restProps } />
    )
  }, [restProps, name, trigger])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
    />
  )
}
